import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useJsApiLoader, Libraries } from '@react-google-maps/api';
import { useQuery } from '@tanstack/react-query';
import { fetchMapsConfig } from '@/shared/api/mapsConfigApi';
import { useAuth } from '@/shared/providers/AuthProvider';

// Declared outside the component so the reference is stable across renders.
// ECOPD-15205 — removed 'places' (all Places SDK calls replaced by BE proxy autocomplete).
// 'geometry' is retained for decodePolyline (google.maps.geometry.encoding.decodePath).
const GOOGLE_MAPS_LIBRARIES: Libraries = ['geometry'];

export const MAPS_CONFIG_QUERY_KEY = ['dispatcher', 'maps-config'] as const;

interface GoogleMapsContextValue {
  isLoaded: boolean;
  loadError?: Error | undefined;
  /** True while the key is being fetched from the BFF. */
  isConfigLoading: boolean;
  /**
   * Set when the key cannot be fetched (401, 503, malformed payload).
   * Deliberately distinct from `loadError`: "we could not get a key" and "Google's
   * script failed to load" are different failures and deserve different copy.
   */
  configError: Error | null;
  /** The key in use, once fetched. Components that hand it to a third-party
   *  Maps component (e.g. the Places autocomplete) read it from here rather
   *  than from a build-time constant. */
  googleMapsApiKey: string | null;
  /** Re-requests the key after `configError`. There is no equivalent for
   *  `loadError`: Google's script is a page singleton and cannot be reloaded
   *  without a page refresh. */
  retryConfig: () => void;
}

const GoogleMapsContext = createContext<GoogleMapsContextValue | undefined>(undefined);

interface GoogleMapsProviderProps {
  children: ReactNode;
}

interface LoaderState {
  isLoaded: boolean;
  loadError: Error | undefined;
}

/**
 * Starts the SDK only once a validated key exists.
 *
 * `useJsApiLoader` is a hook, so it cannot be called conditionally, and it pins
 * its options on first call — Google's SDK is a page singleton and cannot change
 * keys after loading. Isolating it in a child that is MOUNTED only after the key
 * arrives is what lets the key be async without calling the hook with an empty
 * string first (which would load the SDK unauthenticated and then refuse to
 * reload it with the real key).
 */
const GoogleMapsLoader: React.FC<{
  apiKey: string;
  onChange: (state: LoaderState) => void;
}> = ({ apiKey, onChange }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    onChange({ isLoaded, loadError });
  }, [isLoaded, loadError, onChange]);

  return null;
};

/**
 * GoogleMapsProvider
 *
 * Centralised provider for the Google Maps API loader. Ensures a single
 * initialisation across the app, preventing "Loader must not be called again
 * with different options".
 *
 * The browser key is fetched from `GET /drt/api/bff/maps-config` (session-guarded)
 * rather than read from `VITE_GOOGLE_MAPS_API_KEY` at build time, so it no longer
 * ships inside a publicly downloadable bundle and can be rotated without a
 * redeploy. See `shared/api/mapsConfigApi.ts` for what that does and does not buy.
 *
 * Children stay mounted throughout. A failure here must not take down the app:
 * sign-in and every non-map screen keep working, and only map surfaces degrade.
 *
 * Usage:
 * 1. Wrap app in GoogleMapsProvider (done in main.tsx)
 * 2. Use useGoogleMaps() in any component that needs maps
 *
 * @example
 * const { isLoaded } = useGoogleMaps();
 * if (!isLoaded) return <div>Loading maps...</div>;
 * return <GoogleMap ... />;
 */
export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({ children }) => {
  // ECOPD-18366: this provider sits above the router, so it is already mounted
  // on /login. Fetching there gets a 401 (the endpoint is session-guarded), the
  // infinite cache below keeps that failure for the whole tab, and sign-in is an
  // in-app navigation, so nothing re-asks: every map stayed on "Loading..." until
  // a hard refresh. Wait for the session instead. Sign-out and session expiry
  // both reload the page (AuthProvider), which drops the cached key with it.
  const { isAuthenticated } = useAuth();

  const config = useQuery({
    queryKey: MAPS_CONFIG_QUERY_KEY,
    queryFn: ({ signal }) => fetchMapsConfig(signal),
    enabled: isAuthenticated,
    // The SDK cannot swap keys after loading, so re-fetching buys nothing within
    // a page life. A full reload picks up a rotated key.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const refetchConfig = config.refetch;

  const [loader, setLoader] = useState<LoaderState>({ isLoaded: false, loadError: undefined });

  const value = useMemo<GoogleMapsContextValue>(
    () => ({
      isLoaded: loader.isLoaded,
      loadError: loader.loadError,
      isConfigLoading: config.isLoading,
      configError: (config.error as Error | null) ?? null,
      googleMapsApiKey: config.data?.googleMapsApiKey ?? null,
      retryConfig: () => {
        void refetchConfig();
      },
    }),
    [loader, config.isLoading, config.error, config.data, refetchConfig],
  );

  return (
    <GoogleMapsContext.Provider value={value}>
      {config.data && (
        <GoogleMapsLoader apiKey={config.data.googleMapsApiKey} onChange={setLoader} />
      )}
      {children}
    </GoogleMapsContext.Provider>
  );
};

/**
 * useGoogleMaps Hook
 *
 * Access the Google Maps loader state from any component.
 * Must be used within GoogleMapsProvider.
 *
 * @returns {GoogleMapsContextValue} isLoaded, loadError, isConfigLoading, configError, googleMapsApiKey
 * @throws {Error} If used outside GoogleMapsProvider
 *
 * @example
 * const { isLoaded, loadError } = useGoogleMaps();
 * if (loadError) return <div>Error loading maps</div>;
 * if (!isLoaded) return <div>Loading...</div>;
 * return <GoogleMap ... />;
 */
export const useGoogleMaps = (): GoogleMapsContextValue => {
  const context = useContext(GoogleMapsContext);

  if (context === undefined) {
    throw new Error('useGoogleMaps must be used within GoogleMapsProvider');
  }

  return context;
};
